import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Download, LogIn, ShoppingCart, HourglassIcon, Ban, Loader2, ShieldAlert,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  generateAssetDownloadLink, buildDownloadLinkUrl, friendlyError,
  triggerAssetDownload,
} from '@/lib/api';

/**
 * Fase 06D · Botón único y reutilizable para descarga segura.
 *
 * Props:
 *   options: respuesta de get_download_options (has_download, action, asset, product, access, ...)
 *   loading: boolean (mientras se hace fetch inicial)
 *   compact: renderiza una versión compacta (usada en cards)
 *   testIdBase: prefijo para data-testid
 *   className: extra classes en el botón
 */
export function DownloadActionButton({
  options, loading = false, compact = false, testIdBase = 'download-action', className = '',
  onBuyClick, // callback opcional para lanzar checkout personalizado
  onFreeGrant, // callback opcional para grant free
}) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (loading) {
    return <Skeleton className={`h-9 ${compact ? 'w-32' : 'w-56'} rounded-lg`} data-testid={`${testIdBase}-skeleton`} />;
  }
  if (!options) return null;

  const asset = options.asset || null;
  const product = options.product || null;
  const action = options.action;
  const reason = options.reason;
  const price = options.price_cents;
  const currency = options.currency || 'EUR';

  const formatPrice = () => {
    if (!product) return '';
    if (price === 0) return 'Gratis';
    return `${(price / 100).toFixed(2)} ${currency}`;
  };

  const handleDownload = async () => {
    if (!asset?.id) return;
    setBusy(true);
    try {
      // Descarga directa como archivo (sin abrir el reproductor). Ver
      // helper triggerAssetDownload en lib/api.js.
      await triggerAssetDownload(asset.id, asset.file_name || 'contenido');
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const btnBase = `${compact ? 'h-9 px-3 text-xs' : 'h-11 px-5'} rounded-xl font-semibold inline-flex items-center justify-center gap-2 ${className}`;

  if (action === 'download') {
    const isFree = product && Number(price || 0) === 0;
    return (
      <Button
        onClick={handleDownload}
        disabled={busy}
        data-testid={`${testIdBase}-download`}
        className={`${btnBase} bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {isFree ? 'Descargar gratis' : 'Descargar vídeo'}
      </Button>
    );
  }

  if (action === 'free_grant') {
    return (
      <Button
        onClick={() => onFreeGrant?.(product) || navigate(`/checkout/download/${product.id}`)}
        disabled={busy}
        data-testid={`${testIdBase}-free-grant`}
        className={`${btnBase} bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 border-0`}
      >
        <Download className="h-4 w-4" /> Descargar gratis
      </Button>
    );
  }

  if (action === 'buy') {
    return (
      <Button
        onClick={() => onBuyClick?.(product) || navigate(`/checkout/download/${product.id}`)}
        data-testid={`${testIdBase}-buy`}
        className={`${btnBase} bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0`}
      >
        <ShoppingCart className="h-4 w-4" /> Comprar descarga
        <span className="ml-1 text-xs font-normal opacity-90">{formatPrice()}</span>
      </Button>
    );
  }

  if (action === 'login') {
    return (
      <Button
        asChild
        data-testid={`${testIdBase}-login`}
        className={`${btnBase} bg-[var(--ns-text)] text-white hover:bg-[var(--ns-text)]/90 border-0`}
      >
        <Link to="/acceder">
          <LogIn className="h-4 w-4" /> Inicia sesión para descargar
        </Link>
      </Button>
    );
  }

  if (action === 'preparing') {
    return (
      <Badge data-testid={`${testIdBase}-preparing`} className="bg-amber-100 text-amber-800 text-xs px-3 py-1.5 rounded-lg">
        <HourglassIcon className="h-3 w-3 mr-1" /> Descarga próximamente
      </Badge>
    );
  }

  if (action === 'unavailable') {
    if (reason === 'provider_unconfigured') {
      return (
        <Badge data-testid={`${testIdBase}-provider-unconfigured`} className="bg-red-100 text-red-800 text-xs px-3 py-1.5 rounded-lg">
          <ShieldAlert className="h-3 w-3 mr-1" /> Proveedor no configurado
        </Badge>
      );
    }
    if (reason === 'exhausted') {
      return (
        <Badge data-testid={`${testIdBase}-exhausted`} className="bg-gray-100 text-gray-700 text-xs px-3 py-1.5 rounded-lg">
          <Ban className="h-3 w-3 mr-1" /> Límite alcanzado
        </Badge>
      );
    }
    return (
      <Badge data-testid={`${testIdBase}-unavailable`} className="bg-gray-100 text-gray-700 text-xs px-3 py-1.5 rounded-lg">
        <Ban className="h-3 w-3 mr-1" /> Archivo no disponible
      </Badge>
    );
  }

  return null;
}

/**
 * Pequeño wrapper que muestra bajo el botón la metadata del archivo
 * (nombre, formato, tamaño, duración).
 */
export function DownloadAssetMeta({ asset, className = '' }) {
  if (!asset) return null;
  const parts = [];
  if (asset.file_format) parts.push(asset.file_format.toUpperCase());
  if (asset.file_size_bytes) parts.push(formatSize(asset.file_size_bytes));
  if (asset.duration_seconds) parts.push(formatDuration(asset.duration_seconds));
  return (
    <p className={`text-[11px] text-[var(--ns-text-subtle)] ${className}`}>
      {asset.file_name || 'Archivo'}
      {parts.length ? ` · ${parts.join(' · ')}` : ''}
    </p>
  );
}

function formatSize(bytes) {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = Number(bytes); let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}
function formatDuration(secs) {
  if (!secs || secs <= 0) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}
